import { useCallback, useRef, useState } from 'react';

/**
 * Per-application submit lock for MLA review / create flows.
 * Prevents duplicate requests for the same application ID while allowing
 * other rows to stay interactive.
 * Uses a ref for synchronous guard and state to drive UI disabled states.
 */
const useApplicationSubmitLock = (): {
  submittingApplicationIds: Set<number>;
  lockApplication: (id: number) => boolean;
  unlockApplication: (id: number) => void;
  isApplicationSubmitting: (id: number) => boolean;
} => {
  const [submittingApplicationIds, setSubmittingApplicationIds] = useState<Set<number>>(
    () => new Set()
  );
  const submittingApplicationIdsRef = useRef<Set<number>>(new Set());

  const lockApplication = useCallback((id: number): boolean => {
    if (submittingApplicationIdsRef.current.has(id)) return false;

    submittingApplicationIdsRef.current.add(id);
    setSubmittingApplicationIds(new Set(submittingApplicationIdsRef.current));
    return true;
  }, []);

  const unlockApplication = useCallback((id: number): void => {
    submittingApplicationIdsRef.current.delete(id);
    setSubmittingApplicationIds(new Set(submittingApplicationIdsRef.current));
  }, []);

  const isApplicationSubmitting = useCallback((id: number): boolean => (
    submittingApplicationIds.has(id)
  ), [submittingApplicationIds]);

  return {
    submittingApplicationIds,
    lockApplication,
    unlockApplication,
    isApplicationSubmitting
  };
};

export default useApplicationSubmitLock;
